import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const FormulaExplanationLatex = ({ Kr, FF, cieCoordinates, colorPurity, colorTemperature }) => {
  return (
    <div style={{ 
      background: 'white', 
      padding: '30px', 
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '20px', textAlign: 'center' }}>
        📐 Покрокові розрахунки з LaTeX формулами
      </h2>

      {/* Крок 1: Kr */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #2196f3',
        boxShadow: '0 2px 8px rgba(33,150,243,0.2)'
      }}>
        <h3 style={{ color: '#1976d2', marginBottom: '20px', fontSize: '24px' }}>
          Крок 1️⃣: Розрахунок Kr (коефіцієнт світлової ефективності)
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формула:
          </p>
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <BlockMath math="K_r = K_m \times \frac{\int_{380}^{770} \Phi_e(\lambda) \cdot V(\lambda) \, d\lambda}{\int_{380}^{770} \Phi_e(\lambda) \, d\lambda}" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Де:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="K_m = 683 \text{ лм/Вт}" /> — максимальна спектральна світлова ефективність
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\Phi_e(\lambda)" /> — спектральна функція OLED (інтенсивність випромінювання)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="V(\lambda)" /> — фотопічна крива чутливості ока (0 до 1)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\lambda" /> — довжина хвилі (380-770 нм)
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🔬 Алгоритм розрахунку:
            </p>
            <ol style={{ marginLeft: '20px', lineHeight: '2' }}>
              <li>Для кожної довжини хвилі обчислюємо добуток: <InlineMath math="\Phi_e(\lambda) \times V(\lambda)" /></li>
              <li>Інтегруємо (трапецієподібне інтегрування) — це <strong>чисельник</strong></li>
              <li>Інтегруємо загальну інтенсивність <InlineMath math="\Phi_e(\lambda)" /> — це <strong>знаменник</strong></li>
              <li>Ділимо чисельник на знаменник та множимо на <InlineMath math="K_m = 683" /></li>
            </ol>
            
            <div style={{ marginTop: '15px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Трапецієподібне інтегрування:</strong></p>
              <BlockMath math="\int_a^b f(x) \, dx \approx \sum_{i=0}^{n-1} \frac{f(x_i) + f(x_{i+1})}{2} \cdot \Delta x" />
            </div>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Результат:
            </p>
            <div style={{ fontSize: '20px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <InlineMath math={`K_r = ${Kr.toFixed(2)} \\text{ лм/Вт}`} />
            </div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#2e7d32', fontStyle: 'italic' }}>
              💡 Це означає, що 1 Ватт електричної потужності OLED створює {Kr.toFixed(2)} люменів світлового потоку,
              який сприймається людським оком. Ефективність: {((Kr / 683) * 100).toFixed(1)}% від теоретичного максимуму.
            </p>
          </div>
        </div>
      </div>

      {/* Крок 2: FF */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #9c27b0',
        boxShadow: '0 2px 8px rgba(156,39,176,0.2)'
      }}>
        <h3 style={{ color: '#7b1fa2', marginBottom: '20px', fontSize: '24px' }}>
          Крок 2️⃣: Розрахунок FF (Fill Factor — коефіцієнт заповнення)
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формула:
          </p>
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <BlockMath math="FF = \frac{\int_{380}^{770} \Phi_e(\lambda) \cdot S_r^{\text{діода}}(\lambda) \, d\lambda}{\int_{380}^{770} \Phi_e(\lambda) \, d\lambda}" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Де:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="S_r^{\text{діода}}(\lambda)" /> — спектральна чутливість фотодіода
            </li>
            <li style={{ marginBottom: '8px' }}>
              Інші параметри — як у <InlineMath math="K_r" />
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🔬 Алгоритм розрахунку:
            </p>
            <ol style={{ marginLeft: '20px', lineHeight: '2' }}>
              <li>Множимо інтенсивність OLED на чутливість <strong>фотодіода</strong></li>
              <li>Інтегруємо добуток — чисельник</li>
              <li>Ділимо на загальну інтенсивність (знаменник з кроку 1)</li>
            </ol>
            <p style={{ marginTop: '15px', fontStyle: 'italic', color: '#666' }}>
              💡 FF показує, яка частка випромінювання OLED реєструється фотодіодом. 
              Це калібрувальний коефіцієнт для вимірювань.
            </p>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Результат:
            </p>
            <div style={{ fontSize: '20px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <InlineMath math={`FF = ${FF.toFixed(4)}`} />
            </div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#2e7d32', fontStyle: 'italic' }}>
              💡 Це безрозмірний коефіцієнт. Фотодіод реєструє {(FF * 100).toFixed(2)}% від загального випромінювання OLED.
            </p>
          </div>
        </div>
      </div>

      {/* Крок 3: CIE координати */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #ff9800',
        boxShadow: '0 2px 8px rgba(255,152,0,0.2)'
      }}>
        <h3 style={{ color: '#f57c00', marginBottom: '20px', fontSize: '24px' }}>
          Крок 3️⃣: Розрахунок CIE координат (x, y)
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формули:
          </p>
          
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            marginBottom: '15px',
            overflowX: 'auto'
          }}>
            <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>Тристимульні значення:</p>
            <BlockMath math="X = \int_{380}^{770} \Phi_e(\lambda) \cdot \bar{x}(\lambda) \, d\lambda" />
            <BlockMath math="Y = \int_{380}^{770} \Phi_e(\lambda) \cdot \bar{y}(\lambda) \, d\lambda" />
            <BlockMath math="Z = \int_{380}^{770} \Phi_e(\lambda) \cdot \bar{z}(\lambda) \, d\lambda" />
          </div>

          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>Нормалізовані координати:</p>
            <BlockMath math="x = \frac{X}{X + Y + Z}, \quad y = \frac{Y}{X + Y + Z}" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Де:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\bar{x}(\lambda), \bar{y}(\lambda), \bar{z}(\lambda)" /> — функції підбору кольору CIE 1931
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\bar{x}(\lambda)" /> — "червона" складова колірного сприйняття
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\bar{y}(\lambda)" /> — "зелена" складова (збігається з <InlineMath math="V(\lambda)" />)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\bar{z}(\lambda)" /> — "синя" складова колірного сприйняття
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🔬 Алгоритм розрахунку:
            </p>
            <ol style={{ marginLeft: '20px', lineHeight: '2' }}>
              <li>Обчислюємо <strong>тристимульні значення</strong> X, Y, Z через інтегрування</li>
              <li><InlineMath math="X" /> відповідає "червоній" складовій</li>
              <li><InlineMath math="Y" /> відповідає "зеленій" складовій (та яскравості)</li>
              <li><InlineMath math="Z" /> відповідає "синій" складовій</li>
              <li>Нормалізуємо, щоб отримати координати <InlineMath math="x" /> та <InlineMath math="y" /> (від 0 до 1)</li>
              <li>Координата <InlineMath math="z = 1 - x - y" /> (не використовується в діаграмі)</li>
            </ol>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>
              ✅ Результат:
            </p>
            <div style={{ padding: '15px', background: 'white', borderRadius: '6px', marginBottom: '10px' }}>
              <BlockMath math={`X = ${cieCoordinates.X.toFixed(2)}, \\quad Y = ${cieCoordinates.Y.toFixed(2)}, \\quad Z = ${cieCoordinates.Z.toFixed(2)}`} />
            </div>
            <div style={{ padding: '15px', background: 'white', borderRadius: '6px', fontSize: '20px' }}>
              <BlockMath math={`x = ${cieCoordinates.x.toFixed(4)}, \\quad y = ${cieCoordinates.y.toFixed(4)}`} />
            </div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#2e7d32', fontStyle: 'italic' }}>
              💡 Ці координати визначають положення кольору OLED на діаграмі колірності CIE 1931.
            </p>
          </div>
        </div>
      </div>

      {/* Крок 4: Чистота кольору */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #e91e63',
        boxShadow: '0 2px 8px rgba(233,30,99,0.2)'
      }}>
        <h3 style={{ color: '#c2185b', marginBottom: '20px', fontSize: '24px' }}>
          Крок 4️⃣: Розрахунок чистоти кольору (Color Purity)
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формула:
          </p>
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <BlockMath math="m = \frac{a}{a + b}" />
          </div>
          <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
            <p style={{ marginBottom: '8px' }}>Де відстань <InlineMath math="a" /> від білої точки до OLED:</p>
            <BlockMath math="a = \sqrt{(x - x_w)^2 + (y - y_w)^2}" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Де:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="(x, y)" /> — координати кольору OLED
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="(x_w, y_w) = (0.3127, 0.3290)" /> — координати білої точки (illuminant D65)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="a" /> — відстань від білої точки до OLED
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="b" /> — відстань від OLED до межі спектрального локусу
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🔬 Геометрична інтерпретація:
            </p>
            <div style={{ padding: '15px', background: 'white', borderRadius: '6px', fontFamily: 'monospace' }}>
              <pre style={{ margin: 0 }}>{`
    Спектральний локус (межа)
              •  ←─ b ─→  • λdom (домінуюча довжина хвилі)
            ↗
        a ↗
        ↗
  • ← Біла точка (xw, yw)
  
• = Точка кольору OLED (x, y)
              `}</pre>
            </div>
            <ol style={{ marginLeft: '20px', lineHeight: '2', marginTop: '15px' }}>
              <li>Проводимо лінію від білої точки через OLED</li>
              <li>Знаходимо перетин з межею спектрального локусу</li>
              <li>Обчислюємо відстані <InlineMath math="a" /> та <InlineMath math="b" /></li>
              <li>Чистота = <InlineMath math="a / (a + b)" /></li>
            </ol>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Результат:
            </p>
            <div style={{ fontSize: '20px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <InlineMath math={`m = ${(colorPurity.purity * 100).toFixed(1)}\\%`} />
            </div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#2e7d32' }}>
              💡 Шкала чистоти:
            </p>
            <ul style={{ marginLeft: '20px', marginTop: '8px', fontSize: '14px', color: '#2e7d32' }}>
              <li><InlineMath math="m = 0\\%" /> — біле світло (ненасичене)</li>
              <li><InlineMath math="m = 50\\%" /> — помірно насичений колір</li>
              <li><InlineMath math="m = 100\\%" /> — чистий спектральний колір (максимально насичений)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Крок 5: Колірна температура */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #4caf50',
        boxShadow: '0 2px 8px rgba(76,175,80,0.2)'
      }}>
        <h3 style={{ color: '#388e3c', marginBottom: '20px', fontSize: '24px' }}>
          Крок 5️⃣: Розрахунок колірної температури (CCT)
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формула McCamy:
          </p>
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <BlockMath math="n = \frac{x - 0.3320}{0.1858 - y}" />
            <BlockMath math="CCT = 449n^3 + 3525n^2 + 6823.3n + 5520.33" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Що це означає:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              CCT (Correlated Color Temperature) — температура чорного тіла в Кельвінах
            </li>
            <li style={{ marginBottom: '8px' }}>
              Показує, наскільки "тепле" чи "холодне" світло
            </li>
            <li style={{ marginBottom: '8px' }}>
              Апроксимація кубічним поліномом для швидкого розрахунку
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🌡️ Шкала колірної температури:
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '6px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Температура</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Тип світла</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}><InlineMath math="1000-2000 \text{ K}" /></td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>Світло свічки (дуже тепле, червоне)</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}><InlineMath math="2700-3000 \text{ K}" /></td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>Лампа розжарювання (тепле, жовтувате)</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}><InlineMath math="4000-4500 \text{ K}" /></td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>Флуоресцентна лампа (нейтральне)</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}><InlineMath math="5000-5500 \text{ K}" /></td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>Денне світло (холодне)</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}><InlineMath math="6500 \text{ K}" /></td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>Стандарт D65 (яскраве денне світло)</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px' }}><InlineMath math="10000+ \text{ K}" /></td>
                  <td style={{ padding: '8px' }}>Блакитне небо (дуже холодне, синє)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Результат:
            </p>
            <div style={{ fontSize: '20px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <InlineMath math={`CCT = ${colorTemperature} \\text{ K}`} />
            </div>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#2e7d32', fontStyle: 'italic' }}>
              💡 {colorTemperature < 3500 ? '🔥 Тепле світло (жовтувате)' : 
                   colorTemperature < 5000 ? '⚪ Нейтральне світло' : 
                   '❄️ Холодне світло (блакитнувате)'}
            </p>
          </div>
        </div>
      </div>

      {/* Крок 6: Яскравість */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '25px', 
        background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', 
        borderRadius: '12px',
        borderLeft: '5px solid #ffc107',
        boxShadow: '0 2px 8px rgba(255,193,7,0.2)'
      }}>
        <h3 style={{ color: '#f57f17', marginBottom: '20px', fontSize: '24px' }}>
          Крок 6️⃣: Розрахунок яскравості L
        </h3>
        
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: 'bold' }}>
            📝 Формула:
          </p>
          <div style={{ 
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #e9ecef',
            overflowX: 'auto'
          }}>
            <BlockMath math="L = K_r \times I(V) \times \frac{S_{max} \times D^2}{FF \times \tau \times A_{OСВС} \times A_{діода}}" />
          </div>
        </div>

        <div style={{ fontSize: '15px', lineHeight: '1.8', background: 'white', padding: '20px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '15px' }}><strong>Де:</strong></p>
          <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="K_r" /> — коефіцієнт світлової ефективності (з Кроку 1)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="FF" /> — Fill Factor (з Кроку 2)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="I(V)" /> — струм діода при напрузі <InlineMath math="V" />
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="S_{max}" /> — максимальна площа (м²)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="D" /> — діаметр
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="\tau" /> — час прозорості (вікна прозорості)
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="A_{ОСВС}" /> — площа ОСВС
            </li>
            <li style={{ marginBottom: '8px' }}>
              <InlineMath math="A_{діода}" /> — площа фотодіода
            </li>
          </ul>

          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            borderRadius: '8px',
            border: '2px solid #ffc107'
          }}>
            <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              🔬 Алгоритм розрахунку:
            </p>
            <ol style={{ marginLeft: '20px', lineHeight: '2' }}>
              <li>Обчислюємо геометричний коефіцієнт: <InlineMath math="K_{геом} = \frac{S_{max} \times D^2}{\tau \times A_{ОСВС} \times A_{діода}}" /></li>
              <li>Обчислюємо повний коефіцієнт: <InlineMath math="K = \frac{K_r \times K_{геом}}{FF}" /></li>
              <li>Для кожної точки ВАХ: <InlineMath math="L(V) = K \times I(V)" /></li>
            </ol>
            
            <div style={{ marginTop: '15px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <p style={{ marginBottom: '8px' }}><strong>💡 Типові значення яскравості:</strong></p>
              <ul style={{ marginLeft: '20px', fontSize: '14px' }}>
                <li><InlineMath math="0.01 \text{ кд/м}^2" /> — поріг видимості в темряві</li>
                <li><InlineMath math="1 \text{ кд/м}^2" /> — слабке свічення</li>
                <li><InlineMath math="100 \text{ кд/м}^2" /> — комфортна яскравість для дисплея</li>
                <li><InlineMath math="1000 \text{ кд/м}^2" /> — яскравий дисплей</li>
                <li><InlineMath math="10000 \text{ кд/м}^2" /> — дуже яскраве світло</li>
              </ul>
            </div>
          </div>

          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Результат:
            </p>
            <div style={{ fontSize: '16px', padding: '15px', background: 'white', borderRadius: '6px' }}>
              <p>Яскравість залежить від напруги <InlineMath math="V" /> та струму <InlineMath math="I(V)" /></p>
              <p style={{ marginTop: '10px' }}>Дивіться графік "Залежність яскравості від напруги" нижче 📊</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ 
        padding: '25px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: 'white',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(102,126,234,0.3)'
      }}>
        <h3 style={{ marginBottom: '15px', fontSize: '22px' }}>✅ Всі розрахунки виконано!</h3>
        <p style={{ fontSize: '16px', opacity: 0.95, lineHeight: '1.6' }}>
          Прокрутіть вниз, щоб побачити графіки та візуалізацію результатів.
          <br />
          Всі формули відображені у форматі LaTeX для професійного вигляду! 🎓
        </p>
      </div>
    </div>
  );
};

export default FormulaExplanationLatex;
